import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Alert, Button, Field } from '@evogard/ui';
import { api, ok } from '../lib/api.js';
import { useSessionContext } from '../lib/session.js';

/**
 * Corrigir o NOME e o E-MAIL de uma pessoa (30/10/2026).
 *
 * Um formulário só, em três lugares: a ficha do consultor, que chega à pessoa
 * pelo vínculo de vendas, a lista de Usuários e, desde 03/11/2026, a lista de
 * Associados do portal (`IdentityFields` com a rota do associado). Duas
 * cópias seriam duas mensagens sobre o que trocar o e-mail faz, e uma delas
 * envelheceria.
 *
 * Lê a pessoa pela rota dela, e não pelo que o chamador tem na mão: o
 * consultor traz o nome e não o e-mail, e o formulário precisa do e-mail de
 * hoje para não mandar um vazio.
 *
 * É a IDENTIDADE, e não o vínculo — vale em todas as bases da pessoa, e a
 * frase de ajuda diz isso antes do clique.
 */
export function UserIdentityForm({ userId }: { userId: string }) {
  const client = useQueryClient();
  const { data: context } = useSessionContext();

  const person = useQuery({
    queryKey: ['plataforma', 'usuario', context?.currentTenant.id, userId],
    queryFn: async () =>
      ok(
        await api.platform.getUser({ params: { id: userId } }),
        200,
        'Não foi possível carregar a pessoa.',
      ),
  });

  if (person.isPending) {
    return <p className="text-sm text-texto-suave">Carregando o cadastro…</p>;
  }
  if (person.isError) {
    return (
      <Alert
        tone="erro"
        className="break-words"
        title="Não foi possível carregar o nome e o e-mail."
      >
        {person.error.message}
      </Alert>
    );
  }

  return (
    <IdentityFields
      key={`${person.data.nome}|${person.data.email}`}
      initial={person.data}
      save={async (body) =>
        ok(
          await api.platform.editUser({ params: { id: userId }, body }),
          200,
          'Não foi possível salvar o nome e o e-mail.',
        )
      }
      saved={() => {
        void client.invalidateQueries({ queryKey: ['plataforma'] });
        void client.invalidateQueries({ queryKey: ['comercial'] });
      }}
    />
  );
}

/**
 * Os dois campos e o salvar — de quem quer que seja a identidade.
 *
 * `save` é a rota de cada lado (colaborador ou associado): as duas recebem o
 * mesmo corpo e respondem `inviteResent`, e a frase sobre o convite novo é
 * uma só.
 */
export function IdentityFields({
  initial,
  save: send,
  saved,
}: {
  initial: { nome: string; email: string };
  save: (body: { nome: string; email: string }) => Promise<{ inviteResent: boolean }>;
  saved: () => void;
}) {
  const [name, setName] = useState(initial.nome);
  const [email, setEmail] = useState(initial.email);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<{ inviteResent: boolean } | null>(null);

  const unchanged =
    name.trim() === initial.nome && email.trim().toLowerCase() === initial.email;

  const save = useMutation({
    mutationFn: () => send({ nome: name.trim(), email: email.trim() }),
    onSuccess: (result) => {
      setError(null);
      setDone({ inviteResent: result.inviteResent });
      saved();
    },
    onError: (e: Error) => {
      setDone(null);
      setError(e.message);
    },
  });

  return (
    <form
      className="flex flex-col gap-3"
      onSubmit={(e) => {
        e.preventDefault();
        setDone(null);
        save.mutate();
      }}
    >
      {error && <Alert tone="erro">{error}</Alert>}
      {done && (
        <Alert tone="sucesso">
          {done.inviteResent
            ? 'Salvo. O convite de primeiro acesso foi para o e-mail novo.'
            : 'Nome e e-mail salvos.'}
        </Alert>
      )}

      <Field
        label="Nome"
        required
        minLength={2}
        maxLength={120}
        value={name}
        onChange={(e) => {
          setDone(null);
          setName(e.target.value);
        }}
      />
      <Field
        label="E-mail"
        required
        type="email"
        maxLength={200}
        value={email}
        onChange={(e) => {
          setDone(null);
          setEmail(e.target.value);
        }}
        help="É o login. Links já enviados ao e-mail antigo deixam de valer."
      />

      <div className="flex justify-end">
        <Button
          type="submit"
          variant="contorno"
          loading={save.isPending}
          disabled={unchanged}
        >
          Salvar
        </Button>
      </div>
    </form>
  );
}
